import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Performance optimization: Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache after unmount
      
      // Reduce unnecessary refetches
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: true, // Refetch if data is stale
      refetchOnReconnect: 'always', // Always refetch on reconnect
      
      // Network optimization
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: 'online', // Only fetch when online
    },
    mutations: {
      retry: 1,
      networkMode: 'online',
    },
  },
});

// Specific query configurations for different data types
export const queryConfigs = {
  // User/profile data - longer cache time
  user: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },
  
  // Messages - shorter cache time for real-time feel
  messages: {
    staleTime: 0, // Always fresh
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30000, // Poll every 30 seconds
  },
  
  // Groups/settings - medium cache time
  settings: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  
  // Token usage - frequent updates
  tokens: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60000, // Update every minute
  },
  
  // Models catalog - rarely changes
  models: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  }
};

// Query keys factory for consistent query key management
export const queryKeys = {
  // Auth queries
  auth: {
    user: ['auth', 'user'] as const,
    session: ['auth', 'session'] as const,
  },

  // Profile queries
  profiles: {
    all: ['profiles'] as const,
    detail: (id: string) => ['profiles', id] as const,
    current: () => ['profiles', 'current'] as const,
  },

  // Groups queries
  groups: {
    all: ['groups'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['groups', { filters }] as const,
    detail: (id: string) => ['groups', id] as const,
    members: (id: string) => ['groups', id, 'members'] as const,
    userGroups: (userId: string) => ['groups', 'user', userId] as const,
  },

  // Conversations queries
  conversations: {
    all: ['conversations'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['conversations', { filters }] as const,
    detail: (id: string) => ['conversations', id] as const,
    userConversations: (userId: string) =>
      ['conversations', 'user', userId] as const,
    groupConversations: (groupId: string) =>
      ['conversations', 'group', groupId] as const,
  },

  // Messages queries
  messages: {
    list: (conversationId: string) => ['messages', conversationId] as const,
    groupMessages: (groupId: string) => ['messages', 'group', groupId] as const,
  },

  // Prompts queries
  prompts: {
    all: ['prompts'] as const,
    list: (groupId: string) => ['prompts', 'group', groupId] as const,
    detail: (id: string) => ['prompts', id] as const,
  },

  // Token usage queries
  tokenUsage: {
    user: (userId: string, period?: string) =>
      ['tokenUsage', 'user', userId, period] as const,
    group: (groupId: string, period?: string) =>
      ['tokenUsage', 'group', groupId, period] as const,
    conversation: (conversationId: string) =>
      ['tokenUsage', 'conversation', conversationId] as const,
    remaining: (userId: string, groupId: string) =>
      ['remaining-tokens', userId, groupId] as const,
  },

  // Access sessions queries
  accessSessions: {
    active: (userId: string) => ['accessSessions', 'active', userId] as const,
    group: (groupId: string) => ['accessSessions', 'group', groupId] as const,
  },

  // Model configs queries
  modelConfigs: {
    all: ['modelConfigs'] as const,
    enabled: () => ['modelConfigs', 'enabled'] as const,
  },

  // Models queries (for new catalog structure)
  models: {
    catalog: ['models-catalog'] as const,
    forGroup: (groupId: string) => ['models', 'group', groupId] as const,
    default: (groupId?: string) => ['default-model', groupId] as const,
  },

  // Users queries
  users: {
    all: ['users'] as const,
    detail: (id: string) => ['users', id] as const,
    teachers: ['users', 'teachers'] as const,
    students: ['users', 'students'] as const,
  },

  // Stats queries
  stats: {
    dashboard: ['stats', 'dashboard'] as const,
    user: (userId: string) => ['stats', 'user', userId] as const,
    group: (groupId: string) => ['stats', 'group', groupId] as const,
  },

  // System settings queries
  systemSettings: {
    all: ['systemSettings'] as const,
    byKey: (key: string) => ['systemSettings', key] as const,
  },
  
  // Active users tracking
  activeUsers: {
    group: (groupId: string) => ['activeUsers', groupId] as const,
  },
  
  // Typing indicators
  typing: {
    group: (groupId: string) => ['typing', groupId] as const,
  }
};

// Prefetch helper for common data
export const prefetchCommonData = async (userId: string) => {
  // Prefetch user profile
  await queryClient.prefetchQuery({
    queryKey: queryKeys.profiles.current(),
    staleTime: queryConfigs.user.staleTime,
  });
  
  // Prefetch user groups
  await queryClient.prefetchQuery({
    queryKey: queryKeys.groups.userGroups(userId),
    staleTime: queryConfigs.settings.staleTime,
  });
};